import * as React from 'react';
import { FC, useMemo } from 'react';

import type { WalmartEligibility } from 'features/partners-walmart/types';

import Dialog from 'components/dialog';
import { UserDetails } from 'state/auth/hooks/types';
import { t } from 'state/intl/translations';

import {
  CloseButton,
  ConfirmButton,
  DialogContent,
  DialogIconContainer,
  DialogTitle,
  ErrorIcon,
  ModalActionsContainer,
} from './styled';

type EligibilityErrorModalProps = {
  errorCode?: WalmartEligibilityErrors;
  onTryAgain: () => void;
  onDismiss: () => void;
  user?: UserDetails | null;
  eligibility?: WalmartEligibility;
};

export enum WalmartEligibilityErrors {
  LOYALTY_ID_ALREADY_LINKED = 'LOYALTY_ID_ALREADY_LINKED',
  PARTNER_USER_ALREADY_LINKED = 'PARTNER_USER_ALREADY_LINKED',
  WRONG_TIER = 'WRONG_TIER',
  DEFAULT = 'DEFAULT',
}

export const EligibilityErrorModal: FC<React.PropsWithChildren<EligibilityErrorModalProps>> = ({
  errorCode,
  onTryAgain,
  onDismiss,
  user,
  eligibility,
}) => {
  const modalMessageError = useMemo(() => {
    switch (errorCode) {
      case WalmartEligibilityErrors.LOYALTY_ID_ALREADY_LINKED:
        return t('walmartAlreadyLinked', { firstName: user?.details.name });
      case WalmartEligibilityErrors.PARTNER_USER_ALREADY_LINKED:
        const email = eligibility?.linkedAccountEmail;
        return t('walmartPartnerUserAlreadyLinked', { email });
      case WalmartEligibilityErrors.WRONG_TIER:
        return t('walmartAccountLinkingInterrupted');
      default:
        return t('walmartAccountLinkingInterrupted');
    }
  }, [eligibility?.linkedAccountEmail, errorCode, user?.details.name]);

  if (!user) {
    return null; // if you aren't authenticated don't show errors, there is a button on screen to authenticate
  }

  return (
    !!errorCode && (
      <Dialog
        showDialog
        headingComponent={<DialogTitle>{t('walmartErrorLinkingAccounts')}</DialogTitle>}
        bodyComponent={<DialogContent>{modalMessageError}</DialogContent>}
        onDismiss={onDismiss}
        modalAppearanceEventMessage="Walmart+ eligibility error modal"
        image={
          <DialogIconContainer>
            <ErrorIcon />
          </DialogIconContainer>
        }
        actions={
          <ModalActionsContainer>
            {[WalmartEligibilityErrors.DEFAULT, WalmartEligibilityErrors.WRONG_TIER].includes(
              errorCode
            ) ? (
              <>
                <ConfirmButton onPress={onTryAgain}>{t('tryAgain')}</ConfirmButton>
                <CloseButton onPress={onDismiss}>{t('close')}</CloseButton>
              </>
            ) : (
              <ConfirmButton onPress={onDismiss}>{t('okay')}</ConfirmButton>
            )}
          </ModalActionsContainer>
        }
      />
    )
  );
};
